<i18n src="./resources/locales.json"></i18n>
<template>
  <div
    :id="message.id"
    class="conversation-message"
    :class="{
      'flex-reverse': !isReceived,
      sending: isSending && activeConversation
    }"
  >
    <div class="avatar-container" v-if="isReceived">
      <SmsAvatar :contact="contact" :size="40" />
    </div>
    <div class="body-container">
      <div
        class="message-text"
        :class="{
          'own-message': !isReceived,
          'received-message': isReceived,
          'error-message black--text': hasError,
          'expanded-message': message.attachmentsId
        }"
        @contextmenu.prevent="onContextMenu($event)"
      >
        <div v-if="hasError" class="time-error">
          <v-icon size="14" color="red" v-if="hasError">mdi-alert</v-icon>
          <span>{{ $t('messages.errorMessage') }}</span>
        </div>

        <div :class="{ 'white--text': !isReceived }" v-html="messageText"></div>

        <v-progress-circular v-if="isLoading" width="3" class="loading-attachment" color="primary" indeterminate />

        <div v-intersect="onIntersect">
          <div v-if="message.attachments" class="pt-2">
            <div v-for="(attachment, index) in message.attachments" :key="index">
              <div v-if="attachment.isVCF">
                <SmsContactCard
                  :key="index"
                  v-for="(contact, index) in getContactsFromVCard(attachment)"
                  :contact="contact"
                  hide-contact-actions
                  plain
                  class="mt-2"
                />
              </div>
              <v-img
                v-else-if="attachment.isImage"
                :src="attachment.base64String"
                aspect-ratio="1.7"
                class="sms-message__img"
                @click="openImageOnFullScreen(attachment.base64String)"
              />
              <vue-plyr id="media_player" v-else>
                <audio v-if="attachment.isAudio" :src="attachment.base64String" controls playsinline />
                <video v-else-if="attachment.isVideo" :src="attachment.base64String" controls playsinline />
              </vue-plyr>
              <!--template v-else>
                <v-icon x-large>mdi-file-document</v-icon>
                {{ attachment.contentName }}
              </template-->
            </div>
          </div>
        </div>
      </div>
      <div class="sent-date" :class="{ 'customer-sent-date': !isReceived }">
        <div v-if="hasError">
          <span class="retry-text">
            {{ $t('messages.retryMessage') }}
          </span>
          <v-icon size="18" v-if="hasError" class="retry-icon" @click="retrySendMessage()">
            mdi-rotate-right
          </v-icon>
        </div>
        <span v-else>
          <v-progress-circular
            v-if="isSending && activeConversation"
            width="2"
            class="sending-progress"
            color="primary"
            indeterminate
            size="12"
          />
          <span v-show="!isReceived && messageStatus && activeConversation">
            {{ messageStatus }}
          </span>
          <template v-if="!isSending || !activeConversation">
            {{ timeAgo }}
          </template>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import SmsAvatar from '@/components/SmsAvatar/SmsAvatar';
import TimeFormatMixin from '@/mixins/TimeFormatMixin';
import SmsContactCard from '@/components/SmsContactCard/SmsContactCard';
import Contact from '@/classes/v2/Contact';
import ToastMixin from '@/mixins/ToastMixin';
import { MESSAGE_STATUS } from '@/constants/message';

export default {
  name: 'SmsMessage',

  components: {
    SmsAvatar,
    SmsContactCard
  },

  mixins: [TimeFormatMixin, ToastMixin],

  props: {
    message: {
      type: Object,
      default: () => {
        return {};
      }
    }
  },

  data() {
    return {
      isLoading: false,
      isIntersecting: false
    };
  },

  computed: {
    ...mapGetters('user', ['currentUser']),
    ...mapGetters('conversationsV2', ['activeConversation']),
    ...mapGetters('contactsV2', ['getContactByNumber']),
    ...mapGetters('preferences', ['getSettings']),

    isSending() {
      return this.message.lifecycle?.inProcess;
    },

    isSent() {
      return this.message.isFromCurrentUser;
    },

    isReceived() {
      return !this.message.isFromCurrentUser;
    },

    hasError() {
      return this.message.lifecycle.error;
    },

    contact() {
      return this.message.isFromCurrentUser ? this.currentUser : this.getContactByNumber(this.message.from);
    },

    messageText() {
      const result = [];
      this.message.body.split(' ').forEach(textPiece => {
        const cleanTextPiece = this.replaceHtmlEntities(textPiece);
        if (this.isUrl(cleanTextPiece)) {
          const urlWithSchema = cleanTextPiece.startsWith('http') ? cleanTextPiece : `http://${cleanTextPiece}`;
          result.push(`<a href="${urlWithSchema}" target="_blank">${cleanTextPiece}</a>`);
        } else {
          result.push(cleanTextPiece);
        }
      });
      return result.join(' ');
    },

    timeAgo() {
      return this.$moment(this.message.originationTime)
        .tz(this.getSettings.displayPreferences.timeZone)
        .format(`ddd MMM D ${this.getSettings.displayPreferences.timeFormat}`)
        .replace(/\./g, '');
    },

    messageStatus() {
      if (this.message.lifecycle.inProcess) {
        return this.$t('messages.sendingMessage');
      }

      return null;
    }
  },

  watch: {
    'message.statusHistory'() {
      this.showToastError();
    }
  },

  methods: {
    ...mapActions('conversationsV2', ['retrieveAttachments']),

    onContextMenu(event) {
      this.$emit('onContextMenu', { event, message: this.message });
    },

    replaceHtmlEntities(text) {
      // eslint-disable-next-line
      return text.replace(/[\u00A0-\u9999<>\&]/gim, i => '&#' + i.charCodeAt(0) + ';');
    },

    getContactsFromVCard(vCardFile) {
      return Contact.getContactsFromVcfCard(vCardFile);
    },

    isUrl(text) {
      const urlRegex = /^(?!mailto:)(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/;
      const url = new RegExp(urlRegex, 'i');
      return text.length < 2083 && url.test(text);
    },

    retrySendMessage() {
      // TODO Review .. retry files, retry group, etc.
      let messageText = this.message.body;
      if (this.activeConversation.isGroupChat) {
        this.$http.post(`sms/messages/sms/${encodeURIComponent(this.currentUser.number)}/send/group`, {
          body: messageText,
          to: this.activeConversation.contact.number
        });
      } else {
        this.$http
          .post(`sms/messages/sms/${encodeURIComponent(this.currentUser.number)}/send`, {
            body: messageText,
            to: this.activeConversation.contact.number
          })
          .catch(error => {
            console.log(error);
          });
      }
    },

    openImageOnFullScreen(imageSrc) {
      this.$root.$emit('open-image-dialog', imageSrc);
    },

    onIntersect(entries) {
      if (entries[0].isIntersecting) {
        this.loadAttachments();
      }
    },

    loadAttachments() {
      if (this.isLoading || !this.message.attachmentsId || (this.message.attachments && !this.message.isMissingAttachmentContent())) return;

      const { parentConversationId, attachmentsId } = this.message;
      this.isLoading = true;
      this.retrieveAttachments({ conversationId: parentConversationId, attachmentsId }).finally(
        () => (this.isLoading = false)
      );
    },

    showToastError() {
      if (
        this.message.statusHistory.includes(MESSAGE_STATUS.inProcess) &&
        this.message.statusHistory.includes(MESSAGE_STATUS.error) &&
        !this.message.statusHistory.includes(MESSAGE_STATUS.delivered)
      ) {
        this.showError(`${this.$t('messages.errorMessage')} "${this.message.body}"`);
      }
    }
  }
};
</script>

<style scoped>
.loading-attachment {
  margin: 60px auto;
  width: 100% !important;
  display: inline-block;
}
.conversation-message {
  display: flex;
  transition: all 0.5s ease;
}
.conversation-message .message-text {
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 4px;
  box-shadow: 1px 1px 3px #0005;
  max-width: 100%;
  width: fit-content;
  opacity: 0.9;
  transition: all 0.3s ease;
  word-break: break-word;
  text-align: left;
  white-space: pre-wrap;
}
.body-container {
  flex-direction: column;
  display: flex;
}
.expanded-message {
  min-height: 165px;
  min-width: 276px;
}
.conversation-message.sending .body-container .message-text,
.conversation-message .body-container:hover .message-text {
  transform: translateY(-6px);
}
.conversation-message.sending .body-container .sent-date,
.conversation-message .body-container:hover .sent-date {
  opacity: 0.7;
  transform: translateY(-7px);
}
.sending-progress {
  margin: -2px 4px 0 0;
}
.flex-reverse {
  flex-flow: row-reverse !important;
}
.own-message {
  align-self: flex-end;
  background: var(--v-conversation-sent-bubble-base);
  border: 1px solid var(--v-conversation-border-bubble-base);
  color: inherit;
}
.sent-date {
  font-size: 10px;
  white-space: nowrap;
  opacity: 0;
  padding: 4px 0 0 0;
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.conversation-message .body-container:hover .sent-date {
  opacity: 0.7;
  transform: translateY(-7px);
}
.customer-sent-date {
  align-self: flex-end;
}
.customer-sent-date {
  align-self: flex-end;
}
.avatar-container {
  margin: 0 8px;
}
.received-message {
  background: var(--v-conversation-received-bubble-base);
  border: 1px solid var(--v-conversation-border-bubble-base);
  color: inherit;
}
.sms-message__img {
  width: 250px;
  cursor: pointer;
}
.error-message {
  background-color: var(--v-error-lighten5) !important;
}
.retry-icon {
  cursor: pointer;
}
.retry-text {
  color: var(--v-error-base);
  font-size: 10px;
}
.time-error {
  color: var(--v-error-base);
}
.time-error > span {
  font-size: 12px;
  margin-left: 10px;
}
@keyframes blinking {
  0% {
    background: var(--v-conversation-sent-bubble-lighten5);
  }
  100% {
    background: var(--v-conversation-sent-bubble-base);
  }
}
.change-color > div > div > .message-text {
  animation: blinking 2s infinite;
}
#media_player {
  --plyr-color-main: var(--v-primary-base);
}
</style>
